// The New shipment form: what is typed, what each step needs, and the `collectCargo` payload it
// becomes. No screen code here, so it is easy to test.
import {
  handlingsOffered,
  parseBoxBarcode,
  staffLinesFor,
  type CurrencyCode,
} from '@ecobox/domain';
import type { ConfigRecord, PayloadOf, PaymentMethod } from '@ecobox/schema';
import type { City } from './cities';
import { normalizePhone } from './phone';
import type { InvoiceBox, InvoiceView } from './invoice';
import { emptyPiece, pieceProblems, piecesPayload, type PieceDraft } from './prime';

export type Service = 'sea' | 'air' | 'prime';

export const STEP_IDS = ['shipper', 'consignee', 'service', 'boxes', 'contents', 'pay'] as const;
export type StepId = (typeof STEP_IDS)[number];

export const STEP_LABELS: Record<StepId, string> = {
  shipper: 'Shipper',
  consignee: 'Consignee',
  service: 'Service',
  boxes: 'Boxes',
  contents: 'Contents',
  pay: 'Pay',
};

export interface ShipperDraft {
  mode: 'existing' | 'new' | null;
  customer: { id: string; name: string; phoneE164: string } | null;
  name: string;
  phone: string;
  email: string;
}

export interface ConsigneeDraft {
  /** Set when picked from the shipper's address book. */
  consigneeId: string | null;
  name: string;
  phones: string[];
  line1: string;
  line2: string;
  barangay: string;
  province: string;
  postalCode: string;
  landmark: string;
  relationship: string;
  cityId: string;
  cityText: string;
  saveToAddressBook: boolean;
}

export interface BoxDraft extends InvoiceBox {
  /** Why this box needs attention, from the scan. */
  note?: string | undefined;
}

export interface PackingDraft {
  description: string;
  qty: number | undefined;
  valueMinor: number | undefined;
}

export interface PaymentDraft {
  key: number;
  method: PaymentMethod;
  amountMinor: number | undefined;
  reference: string;
}

export interface Draft {
  shipper: ShipperDraft;
  consignee: ConsigneeDraft;
  service: Service | null;
  seasonTags: string[];
  seasonTouched: boolean;
  addOnCodes: string[];
  boxes: BoxDraft[];
  /** Prime pieces (documents and parcels), used instead of `boxes` when the service is Prime. */
  pieces: PieceDraft[];
  packing: PackingDraft[];
  declaredMinor: number | undefined;
  prohibitedAck: boolean;
  payments: PaymentDraft[];
  /** A voucher the customer brought (04 §4.2). The server applies and checks it. */
  voucherCode: string;
  signature: string | null;
  /** The agent who brought the shipment (`agents/{id}`, 03 §3.5), when one was chosen. */
  agentId?: string | null;
}

export function emptyConsignee(): ConsigneeDraft {
  return {
    consigneeId: null,
    name: '',
    phones: [''],
    line1: '',
    line2: '',
    barangay: '',
    province: '',
    postalCode: '',
    landmark: '',
    relationship: '',
    cityId: '',
    cityText: '',
    saveToAddressBook: true,
  };
}

export function initialDraft(): Draft {
  return {
    shipper: { mode: null, customer: null, name: '', phone: '', email: '' },
    consignee: emptyConsignee(),
    service: null,
    seasonTags: [],
    seasonTouched: false,
    addOnCodes: [],
    boxes: [],
    pieces: [emptyPiece(1)],
    packing: [{ description: '', qty: undefined, valueMinor: undefined }],
    declaredMinor: undefined,
    prohibitedAck: false,
    payments: [],
    voucherCode: '',
    signature: null,
  };
}

/** Everything the steps need to know about the branch and the configuration. */
export interface FormContext {
  branchId: string;
  currency: CurrencyCode;
  dial: string | undefined;
  /**
   * Dial code (without +) for consignee phones typed in local form. The counter leaves it out and
   * asks for the full number; the driver's phone passes the destination's code (63 for the
   * Philippines) so "0917 123 4567" works at the door.
   */
  consigneeDial?: string | undefined;
  city: City | undefined;
  /** The branch's country (sector code): the origin of the routes the counter may book. */
  originSectorId: string | undefined;
  /** The routes (03 §3.2), read like the other configuration; staff see every status (D-29). */
  routes: readonly ConfigRecord<'route'>[];
  sizes: ReadonlyMap<string, ConfigRecord<'boxSize'>>;
  hasOpenTill: boolean;
  invoice: InvoiceView;
  /**
   * Why the voucher typed on the Pay step cannot be used (not found, used, expired...), or
   * undefined when there is none or it is fine. The screen that looked the voucher up sets it.
   */
  voucherProblem?: string | undefined;
  /** What a usable voucher pays of the total (it counts as the first payment), else 0. */
  voucherMinor?: number | undefined;
}

/**
 * The handling modes the counter may offer for a destination: those some route from the branch's
 * country to the city's country has (D-28, D-31: staff book open routes only). With
 * no city chosen yet, all three.
 */
export function allowedServices(
  city: City | undefined,
  originSectorId: string | undefined,
  routes: readonly ConfigRecord<'route'>[],
): Service[] {
  if (!city) return ['sea', 'air', 'prime'];
  if (originSectorId === undefined) return [];
  return handlingsOffered(staffLinesFor(originSectorId, city.country, routes));
}

export function boxNeedsDims(size: ConfigRecord<'boxSize'> | undefined): boolean {
  return size?.kind === 'odd';
}

export function paymentsTotal(payments: readonly PaymentDraft[]): number {
  return payments.reduce((n, p) => n + (p.amountMinor ?? 0), 0);
}

/** What is wrong on a step, in words a cashier understands. Empty means the step is complete. */
export function stepProblems(step: StepId, draft: Draft, ctx: FormContext): string[] {
  const out: string[] = [];
  switch (step) {
    case 'shipper': {
      const s = draft.shipper;
      if (s.mode === null) out.push('Find the shipper by phone, or add a new one.');
      else if (s.mode === 'existing' && !s.customer) out.push('Pick the shipper from the list.');
      else if (s.mode === 'new') {
        if (s.name.trim() === '') out.push('Enter the shipper’s name.');
        const phone = normalizePhone(s.phone, ctx.dial);
        if (!phone.ok) out.push(`Shipper phone: ${phone.error}.`);
      }
      return out;
    }
    case 'consignee': {
      const c = draft.consignee;
      if (c.name.trim() === '') out.push('Enter the consignee’s name.');
      const phones = c.phones.filter((p) => p.trim() !== '');
      if (phones.length === 0) out.push('Enter at least one phone number for the consignee.');
      for (const p of phones) {
        const r = normalizePhone(p, ctx.consigneeDial);
        if (!r.ok)
          out.push(`Consignee phone ${p}: ${r.error} (start with the Philippines code +63).`);
      }
      if (c.line1.trim() === '') out.push('Enter the street address.');
      if (c.cityId === '' || !ctx.city) out.push('Choose the destination city from the list.');
      return out;
    }
    case 'service': {
      if (draft.service === null) out.push('Choose Sea, Air or Prime.');
      else if (!allowedServices(ctx.city, ctx.originSectorId, ctx.routes).includes(draft.service)) {
        out.push(
          `${ctx.city?.country ?? 'This country'} is not set up for this service from your country. Choose another service.`,
        );
      }
      return out;
    }
    case 'boxes': {
      if (draft.service === 'prime') return pieceProblems(draft.pieces);
      if (draft.boxes.length === 0) out.push('Scan or type at least one box barcode.');
      draft.boxes.forEach((b, i) => {
        const n = i + 1;
        const size = ctx.sizes.get(b.sizeId);
        if (b.sizeId === '') out.push(`Box ${n}: choose a size.`);
        if (boxNeedsDims(size) && !(b.dims?.l && b.dims.w && b.dims.h)) {
          out.push(`Box ${n}: enter length, width and height (odd-size boxes are priced by size).`);
        }
        if (draft.service !== 'sea' && b.weightKg === undefined) {
          out.push(`Box ${n}: enter the weight (air and Prime are priced by weight).`);
        }
        if (
          size?.maxWeightKg !== undefined &&
          b.weightKg !== undefined &&
          b.weightKg > size.maxWeightKg
        ) {
          out.push(
            `Box ${n}: ${b.weightKg} kg is over the ${size.maxWeightKg} kg limit for size ${b.sizeId}.`,
          );
        }
      });
      return out;
    }
    case 'contents': {
      const rows = draft.packing.filter((p) => p.description.trim() !== '');
      if (rows.length === 0) out.push('List what is in the boxes (at least one item).');
      if (rows.some((p) => !p.qty)) out.push('Every item needs a quantity.');
      if (draft.declaredMinor === undefined) out.push('Enter the declared value (0 if none).');
      const needsDeclared = draft.addOnCodes.some((c) => c.includes('insur'));
      if (needsDeclared && !draft.declaredMinor) {
        out.push('Insurance needs a declared value above zero.');
      }
      if (!draft.prohibitedAck) {
        out.push('Confirm the customer has been told which items are prohibited.');
      }
      return out;
    }
    case 'pay': {
      if (!ctx.invoice.ok) {
        out.push(ctx.invoice.reason);
        return out;
      }
      const total = paymentsTotal(draft.payments);
      if (draft.payments.some((p) => !p.amountMinor)) out.push('Every payment needs an amount.');
      if (total + (ctx.voucherMinor ?? 0) > ctx.invoice.grossMinor) {
        out.push('The payments add up to more than the total. Reduce one of them.');
      }
      if (draft.payments.some((p) => p.method === 'cash') && !ctx.hasOpenTill) {
        out.push('Cash needs an open till. Open your till first, or choose another way to pay.');
      }
      if (draft.voucherCode.trim() !== '' && ctx.voucherProblem) {
        out.push(`The voucher cannot be used. ${ctx.voucherProblem} Clear it, or enter another.`);
      }
      return out;
    }
  }
}

export function firstIncompleteStep(draft: Draft, ctx: FormContext): StepId | null {
  for (const id of STEP_IDS) if (stepProblems(id, draft, ctx).length > 0) return id;
  return null;
}

/** A barcode typed or scanned: valid format, not already in this form. */
export function checkBarcode(
  raw: string,
  existing: readonly BoxDraft[],
): { ok: true; barcode: string } | { ok: false; error: string } {
  const barcode = raw.trim().toUpperCase();
  if (!/^EB\d{10}$/.test(barcode) || parseBoxBarcode(barcode) === null) {
    return {
      ok: false,
      error: `“${barcode}” is not a box barcode (like EB0000000018). Check the label and scan again.`,
    };
  }
  if (existing.some((b) => b.barcode === barcode)) {
    return { ok: false, error: `${barcode} is already in this shipment.` };
  }
  return { ok: true, barcode };
}

export interface PayloadNumbers {
  trackingNo: string;
  receiptNo?: string | undefined;
  /** `typed`: staff typed the tracking number by hand (D-27). Default: generated from a lease. */
  trackingSource?: 'generated' | 'typed' | undefined;
  /** The numbering series a generated number came from (03 §3.5). */
  seriesId?: string | undefined;
}

/** The `collectCargo` payload for the form. Call only when every step is complete. */
export function buildCollectPayload(
  draft: Draft,
  ctx: FormContext,
  numbers: PayloadNumbers,
  expectedGrossMinor: number,
  newCustomerSource: 'walk_in' = 'walk_in',
): PayloadOf<'collectCargo'> {
  const s = draft.shipper;
  const shipper =
    s.mode === 'existing' && s.customer
      ? { customerId: s.customer.id }
      : (() => {
          const phone = normalizePhone(s.phone, ctx.dial);
          return {
            newCustomer: {
              name: s.name.trim(),
              phoneE164: phone.ok ? phone.e164 : '',
              email: s.email.trim(),
              source: newCustomerSource,
            },
          };
        })();
  const c = draft.consignee;
  const phones = c.phones
    .map((p) => normalizePhone(p, ctx.consigneeDial))
    .flatMap((r) => (r.ok ? [r.e164] : []));
  const payments = draft.payments
    .filter((p) => (p.amountMinor ?? 0) > 0)
    .map((p) => ({
      method: p.method,
      amountMinor: p.amountMinor ?? 0,
      reference: p.reference.trim(),
    }));
  return {
    trackingNo: numbers.trackingNo,
    ...(numbers.trackingSource === 'typed' ? { trackingSource: 'typed' as const } : {}),
    ...(numbers.trackingSource !== 'typed' && numbers.seriesId
      ? { seriesId: numbers.seriesId }
      : {}),
    ...(draft.agentId ? { agentId: draft.agentId } : {}),
    shipper,
    consignee: {
      ...(c.consigneeId ? { consigneeId: c.consigneeId } : {}),
      name: c.name.trim(),
      phones,
      address: {
        line1: c.line1.trim(),
        line2: c.line2.trim(),
        barangay: c.barangay.trim(),
        city: ctx.city?.city ?? '',
        province: c.province.trim() || (ctx.city?.province ?? ''),
        postalCode: c.postalCode.trim(),
        landmark: c.landmark.trim(),
      },
      cityId: c.cityId,
      relationship: c.relationship.trim(),
      saveToAddressBook: c.saveToAddressBook,
    },
    service: draft.service ?? 'sea',
    // Prime sends pieces (the server makes their labels); everything else sends boxes.
    boxes:
      draft.service === 'prime'
        ? []
        : draft.boxes.map((b) => ({
            barcode: b.barcode,
            sizeId: b.sizeId,
            ...(b.weightKg !== undefined ? { weightKg: b.weightKg } : {}),
            ...(b.dims?.l && b.dims.w && b.dims.h
              ? { dimsCm: { l: b.dims.l, w: b.dims.w, h: b.dims.h } }
              : {}),
            boxSoldNow: b.sold,
          })),
    pieces: draft.service === 'prime' ? piecesPayload(draft.pieces) : [],
    packingList: draft.packing
      .filter((p) => p.description.trim() !== '' && p.qty)
      .map((p) => ({
        description: p.description.trim(),
        qty: p.qty ?? 1,
        valueMinor: p.valueMinor ?? 0,
      })),
    declaredValueMinor: draft.declaredMinor ?? 0,
    addOnCodes: draft.addOnCodes,
    discountMinor: 0,
    ...(draft.voucherCode.trim() !== '' ? { voucherCode: draft.voucherCode.trim() } : {}),
    seasonTags: draft.seasonTags,
    payments,
    ...(payments.length > 0 && numbers.receiptNo ? { receiptNo: numbers.receiptNo } : {}),
    expectedGrossMinor,
  };
}
